import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  HrailWorkMethod,
  InsertHrailWorkMethodBody,
  UpdateHrailWorkMethodNewImageBody,
  UpdateHrailWorkMethodRemoveImageBody,
} from "../../../../../http/hrail/types/workMethods";
import { useHrailWorkMethodsApi } from "../../../../../http/hrail/useWorkMethods";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import useImageUploader from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface WorkMethodFormBody {
  name: string;
  spanMm: number | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface WorkMethodFormProps {
  workMethod?: HrailWorkMethod;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const WorkMethodForm: FC<WorkMethodFormProps> = ({ workMethod, isDisabled, isLoading, onClose, onCreated, onSaved }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const workMethodApi = useHrailWorkMethodsApi();
  const api = useApi();

  const { component, newImage, removeImage, onSave: onImageSave } = useImageUploader({
    defaultImage: workMethod?.image ?? undefined,
    isLoading: !!isLoading && !workMethod,
    disabled: isDisabled,
  });

  const defaultValues: WorkMethodFormBody = {
    name: workMethod?.name ?? "",
    spanMm: workMethod?.spanMm ?? 0,
    order: workMethod?.order ?? 50,
    visibility: workMethod?.visibility ?? "internal",
  };

  const onSubmit = () => async (formData: WorkMethodFormBody) => {
    setIsSubmitting(true);

    const common: Omit<InsertHrailWorkMethodBody, "image"> = {
      name: formData.name ?? "",
      spanMm: formData.spanMm ?? 0,
      order: formData.order ?? 50,
      visibility: formData.visibility,
    };

    if (workMethod) {
      const update = async (body: UpdateHrailWorkMethodRemoveImageBody | UpdateHrailWorkMethodNewImageBody) => {
        try {
          await workMethodApi.updateAdminHrailWorkMethod(workMethod.id, body);
          dispatch(showSuccessNotification(i18n.translation));
          onImageSave();
          onSaved?.();
          onClose?.();
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      };

      if (newImage) {
        try {
          const signedUrl = await workMethodApi.generateUploadImageUrl(workMethod.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            setIsSubmitting(false);
            return;
          }
          await update({
            ...common,
            image: newImage.name,
          });
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        }
      } else {
        await update({
          ...common,
          removeImage,
        });
      }
    } else {
      try {
        const res = await workMethodApi.insertAdminHrailWorkMethod({
          ...common,
          image: newImage?.name ?? null,
        });
        if (newImage) {
          const signedUrl = await workMethodApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            return;
          }
        }
        dispatch(showSuccessNotification(i18n.translation));
        onImageSave();
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<WorkMethodFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, formState: { errors }, register, reset, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [workMethod]);

        return (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("name", { required: true, minLength: 1 })}
                />
              )}
            </FormField>
            <Controller
              name="spanMm"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.span} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("spanMm", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};



export default WorkMethodForm;
